<template>
  <div>
    <base-header class="pb-6 bg-light">
      <b-row align-v="center" class="py-4">
        <b-col cols="12" lg="6">
          <h6 class="h2 d-inline-block mb-0 text-dark">OTA Inventory Search</h6>
        </b-col>
        <b-col cols="12" lg="6" class="text-right">
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </b-col>
      </b-row>

      <div v-if="noAccess">
        <b-card>
          <b-card-header>
            <p class="h2 mb-2">
              <b-icon-exclamation-triangle-fill variant="danger"></b-icon-exclamation-triangle-fill
              >&nbsp;Restricted
            </p>
          </b-card-header>
          <b-card-body>Access is Restricted</b-card-body>
        </b-card>
      </div>
      <div v-else>

        <b-row>
          <b-col xl="12">
            <b-card no-body>
              <b-card-body>
                <h4>
									<span v-b-tooltip.hover.topright="'Custom Filter: ' + JSON.stringify(filter)"
                  >Filters ({{ filterSize }} active)</span
                  >
                </h4>
                <p data-cy="ota-inventory-results">
                  {{ resultsFound }}
                </p>
                <b-dropdown
									class="float-right mr-2"
									variant="primary"
									toggle-class="text-decoration-none"
                  menu-class="card-body p-0 m-0"
								>
									<template #button-content> 
                    Export {{ totalRows }} records 
                  </template>
									<b-dropdown-item @click="exportData('csv')"> 
                    Export as CSV 
                  </b-dropdown-item>
									<b-dropdown-item @click="exportData('xls')">
										Export as XLS (Excel)
									</b-dropdown-item>
								</b-dropdown>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <b-row>
          <b-col xl="6">
            <b-card no-body>
              <b-card-body>
                <div class="input-group">
                  <b-input-group>
                    <template #prepend>
                      <b-input-group-text :style="ps">ICCID</b-input-group-text>
                    </template>
                    <b-form-input
                        id="iccid"
                        data-cy="ota-iccid-input"
                        type="text"
                        v-model="iccid"
                        @change="inventorySearch"
                    ></b-form-input>
                  </b-input-group>
                </div>
                <div class="input-group">
                  <b-input-group>
                    <template #prepend>
                      <b-input-group-text :style="ps">MSISDN</b-input-group-text>
                    </template>
                    <b-form-input
                        id="msisdn"
                        data-cy="ota-misisdn-input"
                        type="tel"
                        v-model="msisdn"
                        @change="inventorySearch"
                        maxlength="12"
                    ></b-form-input>
                  </b-input-group>
                </div>
                <div class="input-group">
                  <b-input-group>
                    <template #prepend>
                      <b-input-group-text :style="ps">MDN</b-input-group-text>
                    </template>
                    <b-form-input
                        id="mdn"
                        type="tel"
                        v-model="mdn"
                        @change="inventorySearch"
                        maxlength="12"
                    ></b-form-input>
                  </b-input-group>
                </div>
                <div class="input-group">
                  <b-input-group>
                    <template #prepend>
                      <b-input-group-text :style="ps">MIN</b-input-group-text>
                    </template>
                    <b-form-input
                        id="min"
                        type="tel"
                        v-model="min"
                        @change="inventorySearch"
                        maxlength="12"
                    ></b-form-input>
                  </b-input-group>
                </div>
              </b-card-body>
            </b-card>
          </b-col>
          <b-col xl="6">
            <b-card no-body>
              <b-card-body>
                <div class="input-group">
                  <b-input-group>
                    <template #prepend>
                      <b-input-group-text :style="ps">Carrier</b-input-group-text>
                    </template>
                    <!--                    :disabled="!isAdministrator"-->
                    <b-form-select
                        id="carrier"
                        v-model="carrier"
                        :options="carrier_options"
                        @change="inventorySearch"
                    ></b-form-select>
                  </b-input-group>
                </div>
                <div class="input-group">
                  <b-input-group>
                    <template #prepend>
                      <b-input-group-text :style="ps">Enabled</b-input-group-text>
                    </template>
                    <b-form-select
                        id="enabled"
                        v-model="enabled"
                        :options="enabled_options"
                        @change="inventorySearch"
                    ></b-form-select>
                  </b-input-group>
                </div>
                <div class="input-group">
                  <b-input-group>
                    <template #prepend>
                      <b-input-group-text :style="ps">Active</b-input-group-text>
                    </template>
                    <b-form-select
                        id="active"
                        v-model="active"
                        :options="active_options"
                        @change="inventorySearch"
                    ></b-form-select>
                  </b-input-group>
                </div>
                <div class="input-group">
                  <b-input-group>
                    <template #prepend>
                      <b-input-group-text :style="ps">Free text</b-input-group-text>
                    </template>
                    <b-form-input
                        id="freetext"
                        type="text"
                        v-model="freetext"
                        @change="inventorySearch"
                    ></b-form-input>
                  </b-input-group>
                </div>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <b-row>
          <b-col xl="12">
            <b-card no-body>
              <b-card-body>
                <div class="grey-sideborder">
                  <b-table
                      id="inventoryTable"
                      data-cy="ota-inventory-table"
                      head-row-variant="primary-table"
                      class="primary-table w-auto"
                      sticky-header="true"
                      :items="submitLoading ? [] : items"
                      :fields="fields"
                      :sort-by.sync="sortBy"
                      :sort-desc.sync="sortDesc"
                      @sort-changed="sortingChanged"
                      show-empty
                      responsive
                      striped
                      hover
                      outlined>
                    <template #head(actions)="row">
                      <div class="text-left">
                        <base-button
                            size="sm"
                            type="warning"
                            title="Reload Inventory"
                            v-on:click="reloadPage"
                        >
                          <i class="fa fa-recycle"></i> {{ row.value }}
                        </base-button>
                        <b-dropdown
                            size="sm"
                            id="actions-header"
                            variant="light"
                            v-model="clickAction"
                            :hidden="!isPortalAdmin"
                            menu-class="card-body p-0 m-0"
                        >
                          <b-dropdown-item
                              class="text-sm-right link-custom"
                              v-on:click="handleClickEvent('reissueota', '')"
                          >
                            Re-Issue OTA
                          </b-dropdown-item>
                        </b-dropdown>
                      </div>
                    </template>

                    <template #empty="scope">
                      <div v-if="submitLoading" class="text-center">
                        <b-icon icon="three-dots" animation="cylon" font-scale="4"/>
                      </div>
                      <div v-else class="text-center">
                        <b>{{ scope.emptyText }}</b>
                      </div>
                    </template>

                    <template #emptyfiltered="scope">
                      <h4>{{ scope.emptyFilteredText }}</h4>
                    </template>

                    <template #cell(actions)="row">
											<span class="nowrap text-left">
												<base-button
                            :data-cy="`ota-activate-${row.item.iccid}-btn`"
                            size="sm"
                            type="success"
                            v-on:click="handleClickEvent('activate', row.item['iccid'])"
                            :hidden="isReadOnly"
                            title="Activate OTA"
                        >
												<b-icon icon="cloud-upload"></b-icon>
											</base-button>
											<base-button
                          :data-cy="`ota-profile-${row.item.iccid}-btn`"
                          size="sm"
                          type="warning"
                          v-on:click="handleClickEvent('profile', row.item['iccid'])"
                          title="View OTA Records"
                      >
												 <b-icon icon="info-circle"></b-icon>
											</base-button>
                      <base-button
                          size="sm"
                          type="danger"
                          :hidden="isReadOnly"
                          v-on:click="handleClickEvent('reset', row.item['iccid'])"
                          title="Reset OTA to Default"
                      >
                        <b-icon icon="x-circle"></b-icon>
                      </base-button>
											<b-dropdown
                          size="sm"
                          id="actions"
                          variant="light"
                          text=""
                          v-model="clickAction"
                          style="position: static"
                          :hidden="isReadOnly"
                          menu-class="card-body p-0 m-0"
                      >
												<b-dropdown-item
                            v-if="row.item['enabled'] === 'No'"
                            v-on:click="handleClickEvent('enable', row.item['iccid'])"
                        >
													Enable
												</b-dropdown-item>
												<b-dropdown-item
                            v-if="row.item['enabled'] === 'Yes'"
                            v-on:click="handleClickEvent('disable', row.item['iccid'])"
                        >
													Disable
												</b-dropdown-item>
												<b-dropdown-item
                            v-if="row.item['enabled'] === 'Yes'"
                            v-on:click="handleClickEvent('transfer', row.item['iccid'])"
                        >
													Transfer
												</b-dropdown-item>
												<b-dropdown-item
                            v-on:click="handleClickEvent('reset', row.item['iccid'])"
                        >
													Reset defaults
												</b-dropdown-item>
												<b-dropdown-item
                            v-on:click="handleClickEvent('advanced', row.item['iccid'])"
                            hidden
                        >Advanced
												</b-dropdown-item>
												<b-dropdown-item
                            v-if="row.item['enabled'] === 'Yes'"
                            v-on:click="handleClickEvent('triggerota', row.item['iccid'])"
                        >
													Trigger OTA
												</b-dropdown-item>

												<b-dropdown-item
                            v-if="row.item['enabled'] === 'Yes' && JSON.stringify(carrier_options).includes('inland')"
                            v-on:click="handleClickEvent('obliterate', row.item['iccid'])"
                        >
                          <span css="text-danger">Obliterate All Settings</span>
												</b-dropdown-item>

											</b-dropdown>
											</span>
                    </template>

                    <template #cell(iccid)="row">
                      <b-link
                          :data-cy="`ota-iccid-${row.item['iccid']}`"
                          id="iccid"
                          :to="'/ota/profile/:?iccid=' + row.item['iccid']"
                          title="view profile"
                          class="d-flex text-center"
                      >
                        {{ row.value }} <i class="ni ni-curved-next ml-2 mt-1"></i>
                      </b-link>
                    </template>

                    <template #cell(active)="row">
                      <base-button
                          size="sm"
                          style="width: 100px"
                          :type="getStateClass(row.item['active'])"
                          v-on:click="handleClickEvent('profile', row.item['iccid'])"
                      >
                        {{ getActiveValue(row.item["active"], "active") }}
                      </base-button>
                    </template>
                    <template #cell(enabled)="row">
                      <base-button
                          size="sm"
                          style="width: 100px"
                          :type="getStateClass(row.item['enabled'])"
                          v-on:click="handleClickEvent('profile', row.item['iccid'])"
                      >
                        {{ getActiveValue(row.item["enabled"], "enabled") }}
                      </base-button>
                    </template>
                    <template #cell(min)="row">
                      <span class="nowrap">{{ row.item.min }}</span>
                    </template>
                    <template #cell(mdn)="row">
                      <span class="nowrap">{{ row.item.mdn }}</span>
                    </template>
                    <template #cell(msisdn)="row">
                      <span class="nowrap">{{ row.item.msisdn }}</span>
                    </template>
                  </b-table>
                </div>
                <div>
                  <br/>
                  <b-container class="text-xs">
                    <b-row v-if="totalRows > 0" class="justify-content-between">
                      <b-col xl="4" md="3" class="text-xl-left text-left mb-3 ">{{ resultsFound }}</b-col>
                      <b-col xl="4" md="4" class="text-center">
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="totalRows"
                            :per-page="pageSize"
                            :current-page="currentPage"
                            aria-controls="inventoryTable"
                            class="mb-3 justify-content-center"
                        ></b-pagination>
                      </b-col>
                      <b-col xl="4" md="3" class="text-xl-right text-right mb-3">{{ resultsUpdated }}</b-col>
                    </b-row>
                  </b-container>
                </div>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </div>
    </base-header>

    <b-modal id="transferModel" v-model="transferShow" :title="transferTitle" centered>
      Please enter the target ICCID and the reason for transferring below:<br/>
      <div class="input-group">
        <b-input-group>
          <template #prepend>
            <b-input-group-text :style="ps">Target</b-input-group-text>
          </template>
          <b-form-input id="to" type="number" v-model="target_iccid"></b-form-input>
        </b-input-group>
      </div>
      <div class="input-group">
        <b-input-group>
          <template #prepend>
            <b-input-group-text :style="ps">Reason</b-input-group-text>
          </template>
          <b-form-input id="to" type="text" v-model="target_reason"></b-form-input>
        </b-input-group>
      </div>
      <template #modal-footer>
        <div>
          <base-button type="light" size="sm" class="float-right" @click="transferShow = false"
          >Cancel
          </base-button>
        </div>
        <div>
          <base-button
              type="primary"
              size="sm"
              class="float-right"
              @click="handleClickEvent('safetransfer', '')"
          >OK
          </base-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>
<script>
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import moment from "moment";
import otaDashboardService from "@/common/api-services/ota-dashboard-service";
import {exportToCSV, exportToExcel} from "@/common/export-to-csv";
import commonService from "@/common/api-services/common";
import initService from "@/common/api-services/init-service";
import ToastVue from '../../Components/Toast.vue';
import mobileDashboardService from "@/common/api-services/mobile-dashboard-service";

export default {
  components: {
    RouteBreadCrumb,
    ToastVue
  },

  data() {
    return {
      ps: "background-color: rgba(94,114,228) !important;color: #FFFFFF !important;",
      clickAction: "",
      source_iccid: "",
      target_iccid: "",
      transferShow: false,
      transferTitle: "",
      target_reason: "",
      isOtaAdmin: false,
      iccid: "",
      msisdn: "",
      mdn: "",
      min: "",
      carrier: "",
      enabled: "",
      active: "",
      freetext: "",
      filter: [],
      carrier_options: [],
      enabled_options: [
        {value: "0", text: "All"},
        {value: "", text: "Enabled"},
        {value: "2", text: "Disabled"},
      ],
      active_options: [
        {value: "", text: "All"},
        {value: "1", text: "Yes"},
        {value: "0", text: "No"},
      ],
      submitLoading: false,
      showResults: false,
      resultsUpdated: "",
      sortBy: "iccid",
      sortDesc: false,
      pageSize: 10,
      currentPage: 1,
      filterSize: 0,
      currentSort: "",
      computedSort: "asc.iccid",
      totalRows: 0,
      totalPages: 0,
      fields: [
        {key: "actions", label: "", thClass: "bg-primary"},
        {key: "iccid", label: "Iccid", sortable: true, thClass: "bg-primary"},
        //{key: 'state', label: 'State', sortable: true},
        {key: "active", label: "Active", thClass: "bg-primary", sortable: true},
        //{key: 'transferred', label: 'Transferred', sortable: false},
        //{key: 'simType', label: 'Sim Type', sortable: false},
        {key: 'technology', label: 'Technology', sortable: true},
        {key: "min", label: "Min", sortable: true, thClass: "bg-primary"},
        {key: "mdn", label: "Mdn", sortable: true, thClass: "bg-primary"},
        {key: "msisdn", label: "Msisdn", sortable: true, thClass: "bg-primary"},
        {key: "carrier", label: "Carrier", sortable: true, thClass: "bg-primary"},
        {key: "date", label: "Last Modified", sortable: true, thClass: "bg-primary"},
        {key: 'vendor', label: 'Vendor', sortable: true},
      ],
      items: [],
      pageOptions: [
        5,
        10,
        20,
        50,
        {
          value: Number.MAX_SAFE_INTEGER,
          text: "show all",
        },
      ],
      vendors: [],

      //ACCESS ROLES
      isReadOnly: true,
      isEditor: false,
      isAdministrator: false,
      isPortalAdmin: false,
    };
  },
  mounted: function () {
    document.title = "OTA - Inventory";

    /** NOTE: The following needs to be in data:
     isReadOnly: true,
     isEditor: false,
     isAdministrator: false,
     isPortalAdmin: false,
     */

    //set the roles [isReadOnly, isEditor, isAdministrator, isPortalAdmin]
    this.isReadOnly = initService.getUserRoles().includes("OTA_READ");
    this.isEditor = initService.getUserRoles().includes("OTA_READ");
    this.isAdministrator = 
        initService.getUserRoles().includes("CARRIER_ADMIN") || 
        initService.getUserRoles().includes("OTA_ADMIN") ||
        initService.getUserRoles().includes("PORTAL_ADMIN")
    this.isPortalAdmin = initService.getPortalAdminRole();
    this.carrier = initService.getPortalCarrier();
    
    //get the vendor list
    otaDashboardService.getVendors().then(res => {
      if (res.hasOwnProperty("data") && res.data.hasOwnProperty("result")) {
        this.vendors = res.data.result;
      }
    });
    //write to console.
    // console.log("PAGE:", document.title);
    // console.log("isReadOnly:", this.isReadOnly);
    // console.log("isEditor:", this.isEditor);
    // console.log("isAdministrator:", this.isAdministrator);
    // console.log("isPortalAdmin:", this.isPortalAdmin);

    //don't do anything unless the user has access
    if (!this.noAccess) {
      //get the carrier role
      this.readCache();
      this.getCarriers().finally(() => {
        this.inventorySearch().catch(error => {
          console.error(error);
        });
      });
    } else {
      this.$router.push("/restricted").catch(() => {});
    }
  },
  computed: {
		noAccess() {
			// Determine access based on menu link configuration
			let userRoles = initService.getUserRoles();
			let link = this.$store.getters["getLinkByLink"](this.$router.currentRoute.path);

			if (!link) {
				return true;
			} else {
				userRoles = userRoles.filter(role => link.roles.includes(role.toUpperCase()));
				return userRoles.length === 0;
			}
		},
    resultsFound() {
      if (this.totalRows === 0) {
        return "No results found";
      } else if (this.totalRows === 1) {
        return "1 result found";
      } else {
        return `${this.totalRows} results found`;
      }
    },
	},
  watch: {
    currentPage: {
      handler: function (value) {
        this.inventorySearch();
      },
    },
  },

  methods: {

    async exportData(format) {
      let search = JSON.stringify({...this.filter, paging: false});
      // console.log("exportCSV:", search);
      let fileName = `${this.carrier}-ota-inventory-${moment().format("MM-DD-YYYY")}`;
      await otaDashboardService.inventory(search)
          .then(res => {
            if ( res.hasOwnProperty("data") && res.data.hasOwnProperty("result") && res.data.result.hasOwnProperty("inventory") && Object.keys(res.data.result.inventory).length > 0) {
              let arr = [];
              let data = res.data.result.inventory;
              // console.log("exportCSV:", data);
              data.map(item => {
                // console.log("exportCSV:", item);
                let row = {
                  iccid: "",
                  active: "",
                  technology: "",
                  min: "",
                  mdn: "",
                  msisdn: "",
                  carrier: "",
                  modified: "",
                };

                if (item.hasOwnProperty("iccid")) row.iccid = item.iccid;
                if (item.hasOwnProperty("active")) row.active = item.active? "Active" : "Inactive";
                if (item.hasOwnProperty("carrier") && item.carrier.hasOwnProperty("technology")) row.technology = String(item.carrier.technology[0]).toUpperCase();
                if (item.hasOwnProperty("min")) row.min = item.min;
                if (item.hasOwnProperty("mdn")) row.mdn = item.mdn;
                if (item.hasOwnProperty("msisdn")) row.msisdn = item.msisdn;

                if (item.hasOwnProperty("carrier") && item.carrier.hasOwnProperty("display_name")) row.carrier = item.carrier.display_name;
                else if (item.hasOwnProperty("carrier") && item.carrier.hasOwnProperty("name")) row.carrier = item.carrier.name.toString();
                else row.carrier = '';

                row.vendor = item.carrier.vendor;
                row.modified = item.date;
                // let dt = Date.now();
                // row.modified = ''
                // if (item.hasOwnProperty("minutes_ago")) {
                //   let newDt = new Date(dt - item.minutes_ago * 60000);
                //   row.modified = commonService.strToDate(newDt.getTime());
                // }
                arr.push(row);
              });
              // console.log("exportCSV:", arr);
              if (format === "xls") {
                exportToExcel(arr, fileName);
              } else {
                exportToCSV(arr, fileName);
              }
            }
          })
          .catch(error => {
            console.error(error);
            this.$bvToast.toast(error, {
              title: "Error",
              variant: "danger",
              solid: true,
              autoHideDelay: 5000,
              appendToast: true,
            });
          });
      // exportToCSV(this.items, fileName);
    },

    readCache() {
      //get the last saved filter criteria
      let search = localStorage.getItem(this.$route.name);
      // console.log("reading search:", search);
      if (search) {
        search = JSON.parse(search);
        if (search.iccid && search.iccid !== "") this.iccid = search.iccid;
        if (search.msisdn && search.msisdn !== "") this.msisdn = search.msisdn;
        if (search.mdn && search.mdn !== "") this.mdn = search.mdn;
        if (search.min && search.min !== "") this.min = search.min;
        if (search.enabled && search.enabled !== "") this.enabled = search.enabled;
        if (search.active && search.active !== "") this.active = search.active;
        if (search.freetext && search.freetext !== "") this.freetext = search.freetext;
        if (search.carrier && search.carrier !== "") this.carrier = search.carrier;
        if (search.page && search.page !== "") this.currentPage = search.page;
        if (search.sort && search.sort !== "") this.computedSort = search.sort;
      }
    },

    writeCache() {
      //set to current filter criteria
      let search = {
        iccid: this.iccid,
        msisdn: this.msisdn,
        mdn: this.mdn,
        min: this.min,
        enabled: this.enabled,
        active: this.active,
        freetext: this.freetext,
        carrier: this.carrier,
        page: this.currentPage,
        sort: this.computedSort,
      };
      //console.log("writing search:", search);
      localStorage.setItem(this.$route.name, JSON.stringify(search));
    },

    async getCarriers() {
      this.carrier_options = [];

      //set the default carrier value from the group setting
      if (this.isAdministrator || this.isPortalAdmin) {
        this.carrier = "";
      } else {

        //console.log("Group Carrier: ", initService.getPortalCarrier());
        if (this.carrier_options.length > 0) {
          //we need to match the carrier with the list...
          let adm_carrier = initService.getPortalCarrier();

          // if (process.env.VUE_APP_CLIENT==='uetn') {
          //   adm_carrier = "uetn"
          // }

          this.carrier_options.forEach(item => {
           //console.log(item);
            if (item.text.toLowerCase().includes(adm_carrier.toLowerCase())) {
              this.carrier = item.value;
            }
          });
        } else {
          if (process.env.VUE_APP_CLIENT==='uetn') {
            this.carrier = "uetn"
          } else if (process.env.VUE_APP_CLIENT==='cww') {
            this.carrier = "carolinawestwireless"
          } else {
            this.carrier = initService.getPortalCarrier();
          }
        }
        console.log("carrier: " + this.carrier);
      }

      //add all for admins
      if (this.isAdministrator) this.carrier_options.push({value: "", text: "All"});

      //get the list from server
      const data = JSON.stringify({add: 1, files: 1});

      await otaDashboardService.getCarriersList(data).then(res => {

        //get the carrier list from the ota server
        if (res.hasOwnProperty("data") && res.data.hasOwnProperty("result")) {
          this.fetchCarrierList(res)
          //console.log('set selected carrier value to: ', selected_val);
        } else if (res.data.status === false) {
          otaDashboardService.inventory('').then(res => {
            this.fetchCarrierList(res)
          })
        }
      });
      
      this.readCache();
    },

    fetchCarrierList(res) {
      let selected_val = "";
      if (res.data.result) {
        res.data.result.carriers.forEach(item => {
          this.carrier = this.carrier.toLowerCase().replace(" ", "").replace("_", "");

          //add all for admins
          if (item.name !== "") {
            if (this.isAdministrator || this.carrier == "") {
              this.carrier_options.push({value: item.name, text: item.display_name});
            } else {
              //only add carrier for this carrier
              if (
                  item.name.includes(this.carrier) ||
                  item.display_name
                      .toLowerCase()
                      .replace(" ", "")
                      .replace("_", "")
                      .includes(this.carrier)
              ) {
                this.carrier_options.push({value: item.name, text: item.display_name});
                selected_val = item.name;
              }
            }
          }
        });
      }
      if (selected_val != "") {
        this.carrier = selected_val;
      }
    },

    async inventorySearch() {
      await this.searchEvent();

      if (this.submitLoading) {
        //console.log("skipping, current results loading...");
        return;
      }

      this.showResults = false;
      this.submitLoading = true;
      let filterSearch = '{"page":' + this.currentPage + ',"sort":["' + this.computedSort + '"]}';

      if (this.filterSize > 0) filterSearch = JSON.stringify(this.filter);
      await otaDashboardService
          .inventory(filterSearch)
          .then(res => {

            let d = [];
            if (this.currentPage > 1) {
            }
            if (
                res.hasOwnProperty("data") &&
                res.data.hasOwnProperty("result") &&
                res.data.result.hasOwnProperty("inventory") &&
                Object.keys(res.data.result.inventory).length > 0
            ) {
              let dt = Date.now();

              /*
                        if (res.data.result.inventory[0].hasOwnProperty('iccid')) {
                          //console.log('ota v1');
                          //this.fields.splice(this.fields.findIndex(v => v.key === "simSpec"), 1);

                          // if (res.hasOwnProperty('data') &&
                          //   res.data.hasOwnProperty('result') &&
                          //   res.data.result.hasOwnProperty('carriers')) {
                          //   this.carrier_options = [];
                          //   res.data.result.carriers.forEach(item => {
                          //     this.carrier_options.push({value: item.name, text: item.display_name});
                          //   });
                          // }

                        } else {
                          //console.log('ota v2');
                          this.fields.splice(this.fields.findIndex(v => v.key === "enabled"), 1);
                          this.fields.splice(this.fields.findIndex(v => v.key === "msisdn"), 1);
                          this.fields.splice(this.fields.findIndex(v => v.key === "mdn"), 1);
                          this.fields.splice(this.fields.findIndex(v => v.key === "min"), 1);
                          this.fields.splice(this.fields.findIndex(v => v.key === "date"), 1);

                          // if (res.hasOwnProperty('data') &&
                          //   res.data.hasOwnProperty('result') &&
                          //   res.data.result.hasOwnProperty('carriers')) {
                          //   this.carrier_options = [];
                          //   res.data.result.carriers.forEach(item => {
                          //     this.carrier_options.push({value: item.name, text: item.display_name});
                          //   });
                          // } else {
                          //   let carrierStr = `${process.env.VUE_APP_OTA}`;
                          //   this.carrier_options.push({value: carrierStr, text: carrierStr.toUpperCase()});
                          // }
                          //
                        }
                       */

              Object.keys(res.data.result.inventory).forEach(item => {
                //res.data.result.inventory.forEach(item => {

                item = res.data.result.inventory[item];

                let row = [];
                if (item.hasOwnProperty("iccid")) {
                  row.iccid = item.iccid;
                }

                row.vendor = item.carrier.vendor
                // } else {
                //   row.iccid = item.toString();
                //   item = null;
                //   item = res.data.result.inventory[row.iccid];
                // }

                if (item.hasOwnProperty("type")) row.simType = item.type;
                else row.simType = "physical";

                if (item.hasOwnProperty("simspec")) row.simType = item.simspec;
                else row.simType = "physical"; //removed simSpec column

                if (item.hasOwnProperty("active")) row.active = item.active ? "Yes" : "No";

                if (item.hasOwnProperty("ota") && item.ota.hasOwnProperty("active"))
                  row.active = item.ota.active ? "Yes" : "No";

                if (item.hasOwnProperty("enabled")) row.enabled = item.enabled ? "Yes" : "No";

                if (item.hasOwnProperty("disabled")) row.enabled = item.disabled ? "No" : "Yes";

                //set vendor
                if (item.hasOwnProperty("carrier") && item.carrier.hasOwnProperty("vendor")) {
                  // row.vendor = String(item.carrier.vendor).toUpperCase();
                  row.vendor = this.vendors.find(vendor => vendor.name === item.carrier.vendor).display_name;
                }

                //set technology
                if (item.hasOwnProperty("carrier") && item.carrier.hasOwnProperty("technology")) row.technology = String(item.carrier.technology[0]).toUpperCase();

                if (item.hasOwnProperty("transferred")) row.transferred = item.transferred;
                if (item.hasOwnProperty("msisdn")) row.msisdn = item.msisdn;
                if (item.hasOwnProperty("mdn")) row.mdn = item.mdn;
                if (item.hasOwnProperty("min")) row.min = item.min;
                if (item.hasOwnProperty("carrier") && item.carrier.hasOwnProperty("display_name"))
                  row.carrier = item.carrier.display_name;
                else if (item.hasOwnProperty("carrier")) row.carrier = item.carrier.toString();

                if (item.hasOwnProperty("minutes_ago")) {
                  // let newDt = new Date(dt - item.minutes_ago * 60000);
                  // row.date = commonService.strToDate(newDt.getTime());
                  row.date = item.date;
                }
                d.push(row);
              });
              //console.log(d)
              this.items = d;
              //this.currentPage = res.data.result.count.page;
              if (res.data.result.count.pages) this.totalPages = res.data.result.count.pages;

              if (
                  res.data.result.count &&
                  res.data.result.count.hasOwnProperty("total") &&
                  res.data.result.count.total.hasOwnProperty("filtered")
              )
                this.totalRows = res.data.result.count.total.filtered;

              if (res.data.result.count && res.data.result.count.hasOwnProperty("filtered")) {
                this.totalRows = res.data.result.count.filtered.total;
                this.totalPages = res.data.result.count.filtered.pages;
              }

              //this.totalRows = d.length;
              this.showResults = true;
              //this.totalPages = Math.ceil(this.totalRows / this.pageSize);
              //this.resultsFound = `Page ${this.currentPage} of ${this.totalPages}<br>${this.totalRows} record(s) found`;
            } else {
              this.items = null;
              this.totalRows = 0;
            }
            // if (this.items.length > 0) {
            //   this.sortTableData();
            // }
          })
          .catch(e => {
            if (process.env.NODE_ENV !== "production") console.error(e);
            this.items = null;
            this.totalRows = 0;
          })
      this.submitLoading = false;
      this.resultsUpdated = "Last Updated: " + moment().format("MM/DD/YYYY hh:mm:ss");
    },

    async reloadPage() {
      this.items = null;
      this.totalRows = 0;
      this.currentPage = 1;
      await this.inventorySearch();
    },

    async searchEvent() {
      //build out the new search filter
      let json = new Object();

      //json.page = this.currentPage;
      if (this.currentPage && this.currentPage !== "") {
        json.page = this.currentPage;
      } else {
        json.page = 1;
      }

      //json.sort = [this.computedSort];
      json.sort = [];
      if (this.computedSort && this.computedSort !== "") {
        //json.sort = "['" + this.computedSort + "']";
        json.sort.push(this.computedSort);
      } else {
        json.sort.push("asc.iccid");
        //json.sort="['asc.iccid']";
      }

      json.filter = {};
      if (this.iccid && this.iccid !== "") {
        this.iccid = this.iccid.substring(0, 20);
        json.filter.iccid = this.iccid;
      }
      if (this.msisdn && this.msisdn !== "") {
        this.msisdn = this.formatPhone(this.msisdn);
        json.filter.msisdn = this.msisdn;
      }
      if (this.mdn && this.mdn !== "") {
        this.mdn = this.formatPhone(this.mdn);
        json.filter.mdn = this.mdn;
      }
      if (this.min && this.min !== "") {
        this.min = this.formatPhone(this.min);
        json.filter.min = this.min;
      }
      if (this.carrier && this.carrier !== "") {
        json.filter.carrier = this.carrier;
      } else {
        //json.filter.carrier = `${process.env.VUE_APP_OTA}`;
        //json.filter.type="psim"; //v2 only
      }

      if (this.enabled && this.enabled !== "") {
        json.filter.enabled = this.enabled;
      } // else {
      //   json.filter.enabled = "0"; //v1=0,v2=*
      // }
      if (this.active && this.active !== "") {
        json.filter.active = this.active;
      }
      if (this.freetext && this.freetext !== "") {
        json.filter.free_text = this.freetext;
      }
      this.filterSize = Object.keys(json.filter).length;
      this.filter = json;
      this.writeCache();
    },

    async sortingChanged(ctx) {
      // ctx.sortBy   ==> Field key for sorting by (or null for no sorting)
      // ctx.sortDesc ==> true if sorting descending, false otherwise
      //format ex. [desc.min]
      if (ctx.sortBy) {
        if (ctx.sortDesc) {
          this.computedSort = "desc." + ctx.sortBy;
        } else {
          this.computedSort = "asc." + ctx.sortBy;
        }
      } else {
        this.computedSort = "asc.iccid";
      }
      await this.inventorySearch();
    },

    sortTableData() {
      let sortBy = this.sortBy;
      let sortDesc = this.sortDesc;
      let newItems = this.items.sort((a, b) => {
        let comparision_a = a[sortBy];
        let comparision_b = b[sortBy];
        if (sortBy === "date") {
          comparision_a = new Date(a[sortBy]).getTime();
          comparision_b = new Date(b[sortBy]).getTime();
        }
        if (comparision_a === comparision_b) {
          return 0;
        }
        if (sortDesc) {
          return comparision_a < comparision_b ? 1 : -1;
        } else {
          return comparision_a > comparision_b ? 1 : -1;
        }
      })
      // console.log(newItems)
      // this.items = newItems;
    },

    async handleClickEvent(action, iccid) {
      //console.log(action,iccid);
      let result = ``,
          msg = ``;
      try {
        switch (action) {
          case "profile":
            this.$router.push({path: `/ota/profile/:?iccid=${iccid}`});
            break;

          case "activate":
            this.$router.push({path: `/ota/activate/:?iccid=${iccid}`});
            break;

          case "obliterate":
            mobileDashboardService.simple_getimsifromiccid(iccid).then(res => {
              let imsi = res.data.message.imsi;
              mobileDashboardService.simple_terminate(imsi).then(res => {
                let msg = String(res.data.message).toUpperCase();
                this.showToasterMsg("The obliterate action was completed: " + iccid + msg);
              }).catch(function (error) {
                this.showToasterMsg('The obliterate action failed with message:"' + e.message + '"', false, "danger");
              });
            }).catch(function (error) {
              this.showToasterMsg('The obliterate action failed with message:"' + e.message + '"', false, "danger");
            });
            return;

          case "enable":
            // Enable 89000000000000000001
            // Check the box below to also reset the OTA to default values.
            // Reset defaults

            await otaDashboardService
                .enable(iccid)
                .then(res => {
                  if (res.data.hasOwnProperty("error")) {
                    throw res.data.error + ": " + res.data.error_string;
                  } else {
                    //now reset the iccid
                    otaDashboardService.reset(iccid).then(res => {
                      if (res.data.hasOwnProperty("error")) {
                        throw res.data.error + ": " + res.data.error_string;
                      }
                    });
                    this.showToasterMsg("The action was successful for: " + iccid);
                  }
                })
                .catch(e => {
                  this.showToasterMsg('The action failed with message:"' + e + '"', false, "danger");
                });
            break;

          case "disable":
            let reason = "CUST REQUEST";
            await otaDashboardService
                .disable(iccid, reason)
                .then(res => {
                  if (res.data.hasOwnProperty("error")) {
                    throw res.data.error + ": " + res.data.error_string;
                  } else {
                    this.showToasterMsg("The action was successful for: " + iccid);
                  }
                })
                .catch(e => {
                  this.showToasterMsg('The action failed with message:"' + e + '"', false, "danger");
                });
            break;

          case "safetransfer":
            //console.log('safetransfer src:', this.source_iccid, " target:", this.target_iccid);
            this.transferShow = false;
            await otaDashboardService
                .transfer(this.source_iccid, this.target_iccid, this.target_reason)
                .then(res => {
                  if (res.data.hasOwnProperty("error")) {
                    throw res.data.error + ": " + res.data.error_string;
                  } else {
                    this.showToasterMsg(
                        "The action was successful from: " +
                        this.source_iccid +
                        " to " +
                        this.target_iccid
                    );
                  }
                })
                .catch(e => {
                  this.showToasterMsg('The action failed with message:"' + e + '"', false, "danger");
                });
            this.source_iccid = "";
            this.target_iccid = "";
            this.target_reason = "";
            this.transferTitle = "";

            break;
          case "transfer":
            this.source_iccid = iccid;
            this.transferTitle = "Transfer " + iccid;
            this.target_reason = "CUST TRANSFER";
            this.transferShow = true;

            //console.log('transfer src:',this.source_iccid , " target:", this.target_iccid);

            break;
          case "reset":
            msg =
                "This will reset the SIM to default provisioning, and may result in loss of voice, 2G, and 3G service " +
                "until reprovisioned with an active line of service. Do you want to continue?";
            this.$bvModal
                .msgBoxConfirm(msg, {
                  title: "Reset " + iccid,
                  size: "sm",
                  buttonSize: "sm",
                  okVariant: "danger",
                  okTitle: "OK",
                  cancelTitle: "CANCEL",
                  footerClass: "p-2",
                  hideHeaderClose: false,
                  centered: true,
                })
                .then(value => {
                  if (value === true) {
                    otaDashboardService
                        .reset(iccid)
                        .then(res => {
                          if (res.data.hasOwnProperty("error")) {
                            throw res.data.error + ": " + res.data.error_string;
                          } else {
                            this.showToasterMsg("The action was successful for: " + iccid);
                          }
                        })
                        .catch(e => {
                          this.showToasterMsg(
                              'The action failed with message:"' + e + '"',
                              false,
                              "danger"
                          );
                        });
                  }
                })
                .catch(err => {
                  console.error(err);
                });

            break;

          case "reissueota":
            msg =
                "This will reissue OTA to all active SIMs according to the filter above. Do you want to continue?";
            this.$bvModal
                .msgBoxConfirm(msg, {
                  title: "Reissue OTA",
                  size: "sm",
                  buttonSize: "sm",
                  okVariant: "danger",
                  okTitle: "OK",
                  cancelTitle: "CANCEL",
                  footerClass: "p-2",
                  hideHeaderClose: false,
                  centered: true,
                })
                .then(value => {
                  if (value === true) {
                    otaDashboardService
                        .reissueota()
                        .then(res => {
                          if (res.data.hasOwnProperty("error")) {
                            throw res.data.error + ": " + res.data.error_string;
                          } else {
                            this.showToasterMsg("The action was successful for: " + iccid);
                          }
                        })
                        .catch(e => {
                          this.showToasterMsg(
                              'The action failed with message:"' + e + '"',
                              false,
                              "danger"
                          );
                        });
                  }
                })
                .catch(err => {
                  console.error(err);
                });

            break;
          case "advanced":
            //??
            break;
          case "triggerota":
            await otaDashboardService
                .trigger(iccid)
                .then(res => {
                  if (res.data.hasOwnProperty("error")) {
                    throw res.data.error + ": " + res.data.error_string;
                  } else {
                    this.showToasterMsg("The action was successful for: " + iccid);
                  }
                })
                .catch(e => {
                  this.showToasterMsg('The action failed with message:"' + e + '"', false, "danger");
                });

            break;
          default:
            break;
        }

        // this.reloadPage();
        await this.inventorySearch();

        //
        // if (!result || result === "" || result.includes('error')) {
        //   //throw error
        //   throw 'An error occurred for that transaction. Details:' + JSON.stringify(result);
        // } else {
        //   //show good message
        //   this.showToasterMsg('action: ' + action + ' on iccid: ' + iccid + ' completed successfully', true, 'success');
        // }
      } catch (e) {
        //show error
        console.error(e);
        //this.showToasterMsg(e.message, true, 'danger');

        return;
      }
    },

    showToasterMsg(message, append = false, variant = "success") {
      //setup for alert
      this.$toast({
        component: ToastVue,
        props: {
          description: message
        }
      }, {
        type: variant
      })
    },

    getStateClass(state) {
      let sstate = `${state}`; //convert to string
      sstate = sstate.toLowerCase();
      let sclass = "";
      if (sstate === "active" || sstate === "enabled" || sstate === "yes") {
        sclass = "success";
      } else if (sstate === "no" || sstate === "disabled" || sstate === "not active") {
        sclass = "danger";
      } else {
        sclass = "";
      }
      return sclass;
    },

    getActiveValue(val, field) {
      let sstate = `${val}`; //convert to string
      sstate = sstate.toLowerCase();
      if (sstate === "active" || sstate === "enabled" || sstate === "yes") {
        if (field === "active") return "ACTIVE";
        else return "ENABLED";
      } else if (sstate === "no" || sstate === "disabled" || sstate === "not active") {
        if (field === "active") return "INACTIVE";
        else return "DISABLED";
      } else {
        return "";
      }
    },

    getStateValue(val, field) {
      let sstate = `${val}`; //convert to string
      sstate = sstate.toLowerCase();
      if (sstate === "active" || sstate === "enabled" || sstate === "yes") {
        if (field === "state") return "true";
      } else if (sstate === "no" || sstate === "disabled" || sstate === "not active") {
        if (field === "state") return "false";
      } else {
        return "";
      }
    },

    formatPhone(str) {
      return commonService.formatPhone(str);
    },
  },
};
</script>